/**
 * Accepting an invitation or completing a reset.
 *
 * Reachable without signing in, because the whole point is that the holder
 * cannot sign in yet. The token lives only in the URL and is never stored.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiRequestError } from '../lib/api';

export function SetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (password !== confirmation) {
      setError('The two passwords are not the same.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.setPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'The password could not be set.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main
      className="main"
      style={{ maxWidth: 440, margin: '0 auto', justifyContent: 'center', minHeight: '100vh' }}
    >
      <div className="bridge__identity" style={{ marginBottom: 8 }}>
        <span className="bridge__wordmark">NEWZ BRIDGE</span>
        <span className="bridge__subtitle">AI Development Command Center</span>
      </div>
      <h1 className="topstrip__title">Set your password</h1>

      {!token && (
        <p className="small" data-tone="risk" style={{ color: 'var(--risk)' }} role="alert">
          This link has no token in it. Ask for a new one.
        </p>
      )}

      {done ? (
        <div className="panel stack">
          <p className="small">
            Your password is set. Every other session for this account has been ended.
          </p>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => navigate('/login', { replace: true })}
          >
            Go to the Bridge
          </button>
        </div>
      ) : (
        <form className="panel stack" onSubmit={(event) => void submit(event)}>
          <label className="stack stack--tight">
            <span className="label">New password</span>
            <input
              className="btn"
              style={{ justifyContent: 'flex-start', minHeight: 40 }}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
          <label className="stack stack--tight">
            <span className="label">Again</span>
            <input
              className="btn"
              style={{ justifyContent: 'flex-start', minHeight: 40 }}
              type="password"
              autoComplete="new-password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              required
            />
          </label>
          <p className="tiny dim">
            At least 12 characters. Length is what matters; a few unrelated words beat a short
            password with punctuation in it.
          </p>
          {error && (
            <p className="small" data-tone="risk" style={{ color: 'var(--risk)' }} role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            className="btn btn--primary"
            disabled={busy || !token || password.length === 0}
          >
            Set the password
          </button>
        </form>
      )}
    </main>
  );
}
